import { inject, Injectable, isDevMode, OnDestroy } from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentStore, OnStoreInit, tapResponse } from '@ngrx/component-store';
import {
	AccessLevel,
	AccessLevels,
	AppStateService,
	extractOptions,
	UserAccessService,
} from '@siza-staff-care/shared/app-state';
import {
	AdminController,
	AdminScreenConfigDto,
	AggregateResultDto,
	ApiDtoModel,
	CollHolder,
	decode,
	EditDto,
	InfoDto,
	isNewOid,
	LookupType,
	obfuscate,
	obfuscateNew,
	ScreenConfigDto,
} from '@siza-staff-care/shared/data';
import { GenericDialogsService, SaveChangesResult } from '@siza-staff-care/shared/generic-dialogs';
import { GenericErrorHandlerService } from '@siza-staff-care/shared/global-errors';
import {
	AuthStore,
	ControlSecurityService,
	CrudService,
	EditFlags_Create,
	EditFlags_Update,
	EditorSecurityService,
	getAccessLevelForCurrentScreen,
} from '@siza-staff-care/shared/global-services';
import { SnackbarService } from '@siza-staff-care/shared/ui-components/common-ui';
import {
	AllowedEditButtons,
	EditButtonsVm,
	EditListVm,
	GlobalStoreRegistryStore,
	IStore,
	IStore_Internal,
	RouteToRootOptions,
	StoreRegistry,
} from '@siza-staff-care/shared/ui-components/store-registry';
import { assertDefined, diff, makeObservableBool, typedClone } from '@siza-staff-care/shared/utils';
import { NgEventBus } from 'ng-event-bus';
import {
	catchError,
	combineLatest,
	debounceTime,
	defer,
	delay,
	EMPTY,
	exhaustMap,
	filter,
	finalize,
	from,
	map,
	mergeMap,
	Observable,
	of,
	startWith,
	Subject,
	switchMap,
	take,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';
import { ICrudAdaptor } from './crud-adaptor-interface';
import { navigateRouterToSecondaryOutlet, navigateSecondary } from './secondary-outlet-navigator';
import { StandardCrudAdaptor } from './standard-crud-adaptor';

const DEBUG = false;
const DEBUG_DIFF = false;
const DEBUG_CHANGES = false;

export interface GenericEditState<
	TEdit extends EditDto,
	TInfo extends InfoDto,
	TAdditionalState extends object,
	TController extends AdminController,
> {
	createOid: string | undefined;
	oidObfuscated: string | undefined;
	list: TInfo[];
	edit: TEdit | undefined;
	dtos: unknown[] | undefined;
	priorEdit: TEdit | undefined;
	loadingList: boolean;
	loadingEdit: boolean;
	hasUnsavedChanges: boolean;
	isNewInstance: boolean;
	isEditing: boolean;
	form: UntypedFormGroup | undefined;
	formRawValue: unknown | undefined;
	screenConfig: AdminScreenConfigDto | undefined;
	controllerParams?: TController;
	loadCount?: number;
	selectCount: number;
	allowedEditButtons: AllowedEditButtons;
	isActive: boolean;
	isAlwaysSavable: boolean;
	url?: string;
	activatedRoute?: ActivatedRoute;
	operationInProgress?: boolean;
	screenAccessLevel?: AccessLevel;
	confirmWarningNextSave?: boolean;
	insteadOfSelect?: TEdit;
	formPending: boolean;
	forcingClear: boolean;
	ignoreClear: boolean;
	preventClearNewDelete: boolean;
	hasOpened: boolean;
	forceOnEditSetForNext?: boolean;
	suppressHandleLoadError?: boolean;
	canSaveOverride?: boolean;
	warnings?: string[];
	additionalState?: TAdditionalState;
}

const defaultState = {
	createOid: undefined,
	oidObfuscated: undefined,
	list: [],
	edit: undefined,
	dtos: undefined,
	priorEdit: undefined,
	loadingList: false,
	loadingEdit: false,
	hasUnsavedChanges: false,
	isNewInstance: false,
	isEditing: false,
	form: undefined,
	formRawValue: undefined,
	screenConfig: undefined,
	isActive: true,
	isAlwaysSavable: false,
	selectCount: 0,
	formPending: false,
	forcingClear: false,
	ignoreClear: false,
	preventClearNewDelete: false,
	hasOpened: false,
	operationInProgress: false,
	allowedEditButtons: {
		allowNew: true,
		allowDelete: true,
	},
};

export interface SaveResult<T extends EditDto> extends AggregateResultDto {
	dtos: [T, CollHolder];
}

@Injectable()
export abstract class GenericEditStore<
		TEdit extends EditDto,
		TInfo extends InfoDto = TEdit,
		TAdditionalState extends object = Record<string, never>,
		TController extends AdminController = AdminController,
	>
	extends ComponentStore<GenericEditState<TEdit, TInfo, TAdditionalState, TController>>
	implements IStore, OnStoreInit, OnDestroy
{
	protected _dialogs = inject(GenericDialogsService);
	protected _crud = inject(CrudService);
	protected _snackbar = inject(SnackbarService);
	protected _router = inject(Router);
	protected _eventBus = inject(NgEventBus);
	protected _genericErrorHandler = inject(GenericErrorHandlerService);
	protected _appState = inject(AppStateService);
	protected _userAccess = inject(UserAccessService);
	protected _crudAdaptor: ICrudAdaptor = inject(StandardCrudAdaptor);
	protected _controlSecurity = inject(ControlSecurityService);
	private _globalRegistry = inject(GlobalStoreRegistryStore);
	private _auth = inject(AuthStore);
	protected editorSecurity = inject(EditorSecurityService);

	//SCS-248: Having pressed Rota Selection drop-down or Next Rota/Previous button, to go forwards or backwards, with unsaved changes it will repeat that on future saves too even when only pressing save
	public hasCancelled = false; 

	protected storeRegistry? = inject(StoreRegistry, { optional: true });

	constructor() {
		super(defaultState);

		this.setAllowedEditButtons({});
		this.registerStoreOnNextTick();
	}

	//
	// Plain properties
	//
	lookupTypes: LookupType[] = [];
	abstract niceName: string;
	abstract controllerType: string;
	tabName?: string;
	storeKey?: string;
	saveButtonLabel = 'Save';
	protected ignoreEditOid = false;
	protected replaceEditUrl = false;
	protected allowRerouteToEdit = false;
	/**
	 * Allows overriding the complete text used in the unsaved changes dialog.
	 */
	protected saveChangesOverrideText = '';

	// SCS-86: INI file setting SuppressNinoValidation=true is not working on new front end 
	public options: Record<string, unknown> = {};

	get canRouteToCreateNew() {
		return !!this.get().activatedRoute;
	}

	private readonly subCancelSelection = new Subject<string | undefined>();
	readonly cancelSelection$ = this.subCancelSelection.asObservable();

	//
	// Selectors
	//
	readonly list$ = this.select(({ list }) => list);
	readonly edit$ = this.select(({ edit }) => edit);
	readonly dtos$ = this.select(({ dtos }) => dtos);
	readonly priorEdit$ = this.select(({ priorEdit }) => priorEdit);
	readonly hasEdit$ = this.select(({ edit }) => !!edit, { debounce: true });
	readonly loadingList$ = this.select(({ loadingList }) => loadingList);
	readonly loadingEdit$ = this.select(({ loadingEdit }) => loadingEdit);
	readonly isEditing$ = this.select(({ isEditing }) => isEditing, { debounce: true });
	readonly hasUnsavedChanges$ = this.select(({ hasUnsavedChanges }) => hasUnsavedChanges);
	readonly canDelete$ = this.select(({ edit, isNewInstance }) => !!edit && !isNewInstance);
	readonly canClearNewDelete$ = this.select(({ preventClearNewDelete }) => !preventClearNewDelete);
	readonly inProgress$ = this.select((state) => state.loadingEdit || state.loadingList);
	readonly warnings$ = this.select(({ warnings }) => warnings || []);
	readonly hasWarnings$ = this.select(this.warnings$, (warnings) => warnings?.length > 0);

	readonly form$ = this.select(({ form }) => form);
	readonly formRawValue$ = this.select(({ formRawValue }) => formRawValue);
	readonly isNewInstance$ = this.select(({ isNewInstance }) => isNewInstance);
	readonly isExistingInstance$ = this.select(({ isNewInstance }) => !isNewInstance);
	readonly screenConfig$ = this.select(({ screenConfig }) => screenConfig);
	readonly allowedEditButtons$ = this.select(({ allowedEditButtons }) => allowedEditButtons);
	readonly activatedRoute$ = this.select(({ activatedRoute }) => activatedRoute);

	readonly loadCount$ = this.select(({ loadCount }) => loadCount, { debounce: true });
	readonly screenReadOnly$ = this.select(({ screenAccessLevel }) => screenAccessLevel === AccessLevels.ReadOnly);

	readonly additionalState$ = this.select(({ additionalState }) => additionalState);

	readonly obfuscatedOid$ = this.select(({ oidObfuscated }) => oidObfuscated);
	readonly controllerParams$ = this.select(({ controllerParams }) => controllerParams);
	readonly isActive$ = this.select(({ isActive }) => isActive);
	readonly isAlwaysSavable$ = this.select(({ isAlwaysSavable }) => isAlwaysSavable);
	readonly operationInProgress$ = this.select(({ operationInProgress }) => operationInProgress);
	readonly hasNonNewEdit$ = this.select(
		this.edit$,
		this.isNewInstance$,
		(edit, isNewInstance) => !!(edit && !isNewInstance),
	);
	readonly isClear$ = this.select(this.edit$, (edit) => !edit);
	readonly confirmWarningNextSave$ = this.select(({ confirmWarningNextSave }) => !!confirmWarningNextSave);

	readonly isSavable$ = this.select(
		this.hasUnsavedChanges$,
		this.isAlwaysSavable$,
		(hasUnsavedChanges, isAlwaysSavable) => hasUnsavedChanges || isAlwaysSavable,
	);
	readonly formPending$ = this.select(({ formPending }) => formPending);
	readonly opened$ = this.select(({ hasOpened }) => hasOpened);
	readonly preventClearNewDelete$ = this.select(this.canClearNewDelete$, (canClearNewDelete) => !canClearNewDelete);
	readonly suppressHandleLoadError$ = this.select(({ suppressHandleLoadError }) => !!suppressHandleLoadError);
	readonly canSaveOverride$ = this.select(({ canSaveOverride }) => canSaveOverride);
	protected forceAllowSaveOverride$: Observable<boolean | undefined> = of(undefined);

	readonly allowedEditButtons_hideAll$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.hideAll)),
	);

	readonly allowedEditButtons_allowNew$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.allowNew, true)),
	);

	readonly allowedEditButtons_allowSave$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.allowSave, true)),
	);

	readonly allowedEditButtons_allowRestore$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.allowRestore, true)),
	);

	readonly allowedEditButtons_allowDelete$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.allowDelete, true)),
	);

	readonly allowedEditButtons_allowClear$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.allowClear, true)),
	);

	/**
	 * Define a selector to determine whether the user can save the current edit, taking into account whether
	 * it's dirty, and if they're allowed to save by whether the save button is allowed, and also their security
	 * settings (editor flags) for this store.
	 */
	readonly allowSave$ = this.select(
		this.allowedEditButtons_hideAll$,
		this.allowedEditButtons_allowSave$,
		this.hasUnsavedChanges$,
		this.canSaveOverride$,
		this.isNewInstance$,
		(hideAll, allowSave, hasUnsavedChanges, canSaveOverride, isNewInstance) => {
			if (hideAll || !allowSave || !hasUnsavedChanges) {
				return false;
			}

			// Check security
			const editFlags = this.editorSecurity.getEditorSecurity(this);
			const canSave = isNewInstance ? !!(editFlags & EditFlags_Create) : !!(editFlags & EditFlags_Update);

			if (canSaveOverride !== undefined) {
				return canSaveOverride && canSave;
			}

			return canSave;
		},
	);

	readonly allowSave_EditorFlags$ = this.select(
		this.allowedEditButtons_hideAll$,
		this.allowedEditButtons_allowSave$,
		this.canSaveOverride$,
		this.isNewInstance$,
		(hideAll, allowSave, canSaveOverride, isNewInstance) => {
			if (hideAll || !allowSave) {
				return false;
			}

			// Check security
			const editFlags = this.editorSecurity.getEditorSecurity(this);
			const canSave = isNewInstance ? !!(editFlags & EditFlags_Create) : !!(editFlags & EditFlags_Update);

			if (canSaveOverride !== undefined) {
				return canSaveOverride && canSave;
			}

			return canSave;
		},
	);

	private readonly selectCount$ = this.select(({ selectCount }) => selectCount);
	private readonly selectedOid$ = this.select(({ createOid, edit, isNewInstance }) => {
		let oid: string | undefined;
		if (edit) {
			if (isNewInstance) {
				oid = createOid !== undefined ? createOid + '' : undefined;
			} else {
				oid = edit.oid || '';
			}
		}

		return oid;
	});

	readonly hasOpened$ = this.select(({ hasOpened }) => !!hasOpened);

	readonly realOid$ = combineLatest([this.selectedOid$, this.selectCount$]).pipe(
		map(([realOid]) => {
			realOid = realOid || '';
			return realOid;
		}),
	);

	protected isScreenReadonly$() {
		return defer(() => {
			return of(this.isScreenReadonly);
		});
	}

	get isScreenReadonly() {
		const access = getAccessLevelForCurrentScreen(this._router, this._userAccess);
		return access === AccessLevels.ReadOnly;
	}

	private readonly insteadOfSelect$ = this.select(({ insteadOfSelect }) => insteadOfSelect);
	readonly ignoreClear$ = this.select(({ ignoreClear }) => ignoreClear);

	readonly forceOnEditSetForNext$ = this.select(({ forceOnEditSetForNext }) => forceOnEditSetForNext);

	private subSaved = new Subject<boolean>();
	readonly saved$ = this.subSaved.asObservable();

	private subDeleted = new Subject<boolean>();
	readonly deleted$ = this.subDeleted.asObservable();

	private subEditOrCreateComplete = new Subject<boolean>();
	editOrCreateComplete$ = this.subEditOrCreateComplete.asObservable();

	private niceName$ = defer(() => of(this.niceName));
	private saveButtonLabel$ = defer(() => of(this.saveButtonLabel));

	readonly buttonsVm$: Observable<EditButtonsVm> = this.select({
		hideAll: this.allowedEditButtons_hideAll$,
		allowNew: this.allowedEditButtons_allowNew$,
		allowSave: this.allowedEditButtons_allowSave$,
		allowRestore: this.allowedEditButtons_allowRestore$,
		allowDelete: this.allowedEditButtons_allowDelete$,
		allowClear: this.allowedEditButtons_allowClear$,

		allowSave_EditorFlags: this.allowSave_EditorFlags$,

		hasEdit: this.hasEdit$,
		isSavable: this.isSavable$,
		formPending: this.formPending$,
		hasUnsavedChanges: this.hasUnsavedChanges$,
		isNewInstance: this.isNewInstance$,
		canDelete: this.canDelete$,
		canClearNewDelete: this.canClearNewDelete$,

		inProgress: this.inProgress$,

		niceName: this.niceName$,
		saveButtonLabel: this.saveButtonLabel$,
	});

	readonly editListVm$: Observable<EditListVm> = this.select({
		list: this.list$,
		edit: this.edit$,
		realOid: this.realOid$,
		loadingList: this.loadingList$,
		loadingEdit: this.loadingEdit$,
		isEditing: this.isEditing$,
		loadCount: this.loadCount$,
		hasOpened: this.hasOpened$,
		hasEdit: this.hasEdit$,
	});

	readonly editVm$ = this.select({
		edit: this.edit$,
		loadingEdit: this.loadingEdit$,
		inProgress: this.inProgress$,
	});

	get isForcingClear() {
		return this.get().forcingClear;
	}

	/**
	 * Synchronously gets whether the current edit is new or not.
	 */
	get isNewInstance() {
		const { isNewInstance } = this.get();
		return isNewInstance;
	}

	/**
	 * Synchronously gets the list.
	 */
	get list() {
		const { list } = this.get();
		return list;
	}

	/**
	 * Synchronously returns the obfuscated oid.
	 */
	get oidObfuscated() {
		return this.get().oidObfuscated;
	}

	/**
	 * Synchronously returns the decoded version of the obfuscated oid.
	 */
	get oidDecoded() {
		if (this.oidObfuscated) {
			return decode(this.oidObfuscated);
		}

		return '';
	}

	get isReadOnly() {
		return this._controlSecurity.isCurrentScreenReadOnly();
	}

	get hasOpened() {
		return this.get().hasOpened;
	}

	get activatedRoute() {
		return this.get().activatedRoute;
	}

	//
	// Updaters
	//

	protected readonly patchAdditional = this.updater((state, additional: Partial<TAdditionalState>) => {
		return {
			...state,
			additionalState: {
				...state.additionalState,
				...additional,
			} as TAdditionalState,
		};
	});

	/**
	 * Allows setting an "edit" object directly, bypassing the CRUD select from the server.
	 */
	protected readonly setInsteadOfSelect = this.updater((state, insteadOfSelect: TEdit | undefined) => ({
		...state,
		insteadOfSelect,
	}));

	readonly setList = this.effect((list$: Observable<TInfo[] | null>) =>
		list$.pipe(
			tap((list) => {
				this.onListSet(list);
				this.patchState({ list: list || [] });
			}),
		),
	);

	readonly setDtos = this.updater((state, dtos: unknown[] | undefined) => {
		return {
			...state,
			dtos,
		};
	});

	readonly setLoadingList = this.updater((state, loadingList: boolean) => ({
		...state,
		loadingList,
	}));

	readonly setLoadingEdit = this.updater((state, loadingEdit: boolean) => ({
		...state,
		loadingEdit,
	}));

	readonly setIsEditing = this.updater((state, isEditing: boolean) => ({
		...state,
		isEditing,
	}));

	readonly setIsNewInstance = this.updater((state, isNewInstance: boolean) => ({
		...state,
		isNewInstance,
	}));

	readonly setForm = this.updater((state, form: UntypedFormGroup | undefined) => ({
		...state,
		form,
	}));

	readonly setCreateOid = this.updater((state, createOid: string | undefined) => ({
		...state,
		createOid,
	}));

	readonly setScreenConfig = this.updater((state, screenConfig: AdminScreenConfigDto | undefined) => ({
		...state,
		screenConfig,
	}));

	readonly bumpLoadCount = this.updater((state) => {
		return {
			...state,
			loadCount: state.loadCount ? state.loadCount + 1 : 1,
		};
	});

	readonly setAllowedEditButtons = this.updater((state, allowedEditButtons: AllowedEditButtons) => {
		// Ensure the hideAll flag is clear if undefined to prevent buttons from being hidden
		// when hideAll isn't configured.
		if (allowedEditButtons.hideAll === undefined) {
			allowedEditButtons.hideAll = false;
		}

		return {
			...state,
			allowedEditButtons,
		};
	});

	readonly _setOidObfuscated = this.effect((oidObfuscated$: Observable<string | undefined>) =>
		oidObfuscated$.pipe(
			tap((oidObfuscated) => {
				this.patchState({ oidObfuscated });
			}),
		),
	);

	readonly setIsActive = this.updater((state, isActive: boolean) => ({
		...state,
		isActive,
	}));

	readonly setUrl = this.updater((state, url: string | undefined) => ({
		...state,
		url,
	}));

	readonly setIsAlwaysSavable = this.updater((state, isAlwaysSavable: boolean) => ({
		...state,
		isAlwaysSavable,
	}));

	readonly setOperationInProgress = this.updater((state, operationInProgress: boolean) => ({
		...state,
		operationInProgress,
	}));

	/**
	 * Sets a flag to cause the onEditSet method to be called on the next setEdit call.
	 */
	readonly setForceOnEditSetForNext = this.updater((state, forceOnEditSetForNext: boolean) => ({
		...state,
		forceOnEditSetForNext,
	}));

	//
	// Effects
	//

	readonly overrideCanSave = this.effect((canSaveOverride$: Observable<boolean>) =>
		canSaveOverride$.pipe(
			tap((canSaveOverride) => {
				this.patchState({ canSaveOverride });
			}),
		),
	);

	readonly _setEdit = this.effect((edit$: Observable<TEdit | null>) =>
		edit$.pipe(
			withLatestFrom(this.edit$, this.forceOnEditSetForNext$),
			tap(([edit, oldEdit, forceOnEditSetForNext]) => {
				edit = this.transformEdit(edit);
				this.patchState({ edit: edit || undefined });
				if (this.ignoreEditOid || oldEdit?.oid !== edit?.oid || forceOnEditSetForNext || (edit && !edit.oid)) {
					this.onEditSet(edit);
				}

				this.patchState({
					priorEdit: edit ? typedClone(edit) : undefined,
					hasUnsavedChanges: false,
					forceOnEditSetForNext: false,
				});
			}),
		),
	);

	readonly setSuppressHandleLoadError = this.effect((suppressHandleLoadError$: Observable<boolean>) =>
		suppressHandleLoadError$.pipe(
			tap((suppressHandleLoadError) => {
				this.patchState({
					suppressHandleLoadError,
				});
			}),
		),
	);

	readonly setUpdatedEdit = this.effect((edit$: Observable<TEdit>) =>
		edit$.pipe(
			tap((edit) => {
				if (edit) {
					edit.ed = (edit.ed ?? 0) + 1;
				}
				this.patchState({
					edit,
					hasUnsavedChanges: this.protectUnsavedChangesFromBecomingTrueOnReadOnlyScreen(true),
				});
			}),
		),
	);

	readonly setUpdatedEditNoChanges = this.effect((edit$: Observable<TEdit>) =>
		edit$.pipe(
			tap((edit) => {
				if (edit) {
					edit.ed = (edit.ed ?? 0) + 1;
				}
				this.patchState({
					edit,
				});
			}),
		),
	);

	private readonly registerStoreOnNextTick = this.effect((trigger$) =>
		trigger$.pipe(
			delay(0),
			tap(() => {
				this._globalRegistry.registerStore(this);
			}),
		),
	);

	readonly ignoreNextClear = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.setIgnoreClear(true);
			}),
		),
	);

	private readonly setIgnoreClear = this.effect((ignoreClear$: Observable<boolean>) =>
		ignoreClear$.pipe(
			tap((ignoreClear) => {
				this.patchState({ ignoreClear });
			}),
		),
	);

	readonly registerActivatedRoute = this.effect((route$: Observable<ActivatedRoute>) =>
		route$.pipe(
			tap((route) => {
				this.patchState({
					activatedRoute: route,
				});
			}),
		),
	);

	readonly setControllerParameters = this.effect((controllerParams$: Observable<TController>) => {
		return controllerParams$.pipe(
			tap((controllerParams) => {
				this.patchState({
					controllerParams,
				});
			}),
		);
	});

	readonly registerForm = this.effect((form$: Observable<UntypedFormGroup | undefined>) => {
		return form$.pipe(
			switchMap((form) => {
				this.setForm(form);

				if (form) {
					return form.valueChanges.pipe(
						debounceTime(50),
						tap(() => {
							const rawValue = form.getRawValue();
							const isDirty = form.dirty;

							this.patchState({ formRawValue: rawValue });
							this.setHasUnsavedChanges(isDirty);
						}),
					);
				}

				return EMPTY;
			}),
		);
	});

	/**
	 * Force clearing the edit. Used by the deactivate guard when successfully routed.
	 * Just calls a method which can be overridden to handle clearing the edit.
	 *
	 * Implemented for staff-records to force clearing the selected staff master when
	 * re-navigated to from the menu.
	 */
	readonly forceClear = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({ forcingClear: true });
				this.onForceClear();
				this.patchState({ forcingClear: false });
			}),
		),
	);

	readonly open = this.effect((trigger$) => {
		return trigger$.pipe(
			tap(() => {
				this.setLoadingList(true);
			}),
			withLatestFrom(this.controllerParams$),
			exhaustMap(([, controllerParams]) => {
				return this._crudAdaptor
					.open<AdminScreenConfigDto>(this.controllerType, this.lookupTypes, controllerParams)
					.pipe(
						tapResponse(
							(result: AdminScreenConfigDto) => {
								const { objects, options } = result;

								const list = objects;
								this.setList(list);
								this.setScreenConfig(result);

								this.options = extractOptions(options);

								this.afterOpen(result);
								this.patchState({ hasOpened: true });
							},
							(err) => this._genericErrorHandler.handleError(err, this.niceName, 'Opening'),
						),
						finalize(() => this.setLoadingList(false)),
					);
			}),
		);
	});

	readonly editOrCreate = this.effect((obfuscatedOid$: Observable<string>) =>
		obfuscatedOid$.pipe(
			withLatestFrom(this.form$, this.suppressHandleLoadError$),
			// Note: using exhaustMap rather than switchMap as we don't want to overwhelm Jade's limited supply of processes
			// with cancelled requests.
			exhaustMap(([obfuscatedOid, form, suppressHandleLoadError]) => {
				if (this.vetoSelect()) {
					this.signalEditOrCreateComplete(false);
					return EMPTY;
				}

				this.setOidObfuscated(obfuscatedOid);

				const decoded = decode(obfuscatedOid);
				const decodedAsNumber = Number(decoded);
				if (isNaN(decodedAsNumber)) {
					throw new Error('Invalid oid');
				}

				if (DEBUG) console.log(`${this.niceName} editOrCreate ${obfuscatedOid} = ${decoded}`);

				const isCreate = isNewOid(obfuscatedOid);

				const handleResponse = ({ dtos, warnings }: { dtos: TEdit[]; warnings: string[] }) => {
					const edit = dtos[0];

					this.patchState({ warnings: warnings || [] });

					this.setDtos(dtos);
					this.setEdit(edit);
					isCreate ? this.setIsNewInstance(true) : this.setCreateOid(undefined);
					this.bumpLoadCount();

					this.setCreateOid(undefined);

					setTimeout(() => {
						form?.markAsPristine();
						form?.markAsUntouched();
					});

					this.setHasUnsavedChanges(isCreate);
					this.afterEditSet(edit);
					this.onDtosSet(dtos);

					this.signalEditOrCreateComplete(true);
				};

				const handleError = (err) => {
					if (!suppressHandleLoadError) {
						this._genericErrorHandler.handleError(err, this.niceName, 'Getting');
					}
					this.setIsEditing(false);

					this.signalEditOrCreateComplete(false);
				};

				if (isCreate) {
					this.setCreateOid(decoded);

					this.setLoadingEdit(true);
					this.setIsEditing(true);

					return this.onGetCreateParams().pipe(
						// Opportunity to get some additional parameters for the create.
						exhaustMap((createParams) => {
							return this._crudAdaptor.create(this.controllerType, createParams).pipe(
								tapResponse(handleResponse, handleError),
								finalize(() => this.setLoadingEdit(false)),
							);
						}),
					);
				} else {
					// It's a Jade instance OID.
					this.setLoadingEdit(true);
					this.setIsEditing(true);

					return this.onGetSelectParams().pipe(
						withLatestFrom(this.insteadOfSelect$),
						// Opportunity to get some additional parameters for the select.
						exhaustMap(([selectParams, insteadOfSelect]) => {
							if (insteadOfSelect) {
								this.setInsteadOfSelect(undefined);

								this.setLoadingEdit(false);
								this.setEdit(insteadOfSelect);
								this.setIsEditing(true);

								return EMPTY;
							}

							return this._crudAdaptor.select(this.controllerType, obfuscatedOid, selectParams).pipe(
								tapResponse(handleResponse, handleError),
								finalize(() => {
									this.setLoadingEdit(false);
								}),
							);
						}),
					);
				}
			}),
		),
	);

	readonly restore = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.form$),
			tap(([, form]) => {
				const prior = this.get().priorEdit;
				if (prior) {
					this.setEdit(prior);
				}

				form?.markAsUntouched();
				this.onRestore(prior);

				setTimeout(() => {
					form?.markAsUntouched();
					form?.markAsPristine();

					this.setHasUnsavedChanges(false);
				});
			}),
		),
	);

	private preventMutatingActionWhilePaintingSecurity(actionType: string): Observable<void> {
		return this._appState.isPaintingSecurity$.pipe(
			take(1),
			delay(0), // so the signalSaveSuccess(false) happens on the next tick to allow the observable to be subscribed to first.
			switchMap((isPaintingSecurity) => {
				if (isPaintingSecurity) {
					this._dialogs.message({
						title: `Can't ${actionType}`,
						message: `${actionType} is prohibited while painting security.`,
					});

					this.signalSaveSuccess(false);
					return EMPTY;
				}

				return of(undefined);
			}),
		);
	}

	/**
	 * Drives the router to the edit UI for the given obfuscated oid.
	 */
	readonly routeToEdit = this.effect((oid$: Observable<string>) =>
		oid$.pipe(
			withLatestFrom(this.activatedRoute$, this.edit$),
			switchMap(([oid, route]) => {
				if (this.oidObfuscated === oid && this.allowRerouteToEdit) {
					this.refreshCurrentEdit();
					return of(true);
				}

				return from(
					this._router.navigate([oid], {
						relativeTo: route,
						replaceUrl: this.replaceEditUrl,
						queryParamsHandling: 'merge',
						skipLocationChange: true,
						state: {
							// Set a flag to prevent the store-aware-can-deactivate guard from re-routing to the base URL.
							preventDeactivateReroute: true,
						},
					}),
				).pipe(
					tap((routedOk) => {
						if (routedOk) {
							this.setOidObfuscated(oid);
							this.onRouted();
						} else {
							if (oid) {
								const lastSelectedId = this.get().oidObfuscated;
								this.subCancelSelection.next(lastSelectedId);
							}
						}
					}),
				);
			}),
		),
	);

	/**
	 * Drives the router to the edit UI for the given real (non-obfuscated) oid.
	 */
	readonly routeToEditReal = this.effect((oid$: Observable<string | undefined>) =>
		oid$.pipe(
			tap((oid) => {
				if (this.oidDecoded === oid && !this.allowRerouteToEdit) {
					return;
				}

				oid = obfuscate(oid ?? '');
				this.routeToEdit(oid);
			}),
		),
	);

	/**
	 * Drives the router to the edit UI for the given real (non-obfuscated) oid, setting the replaceUrl flag to true.
	 */
	readonly routeToEditReal_replaceUrl = this.effect((oid$: Observable<string | undefined>) =>
		oid$.pipe(
			tap((oid) => {
				const oldReplace = this.replaceEditUrl;
				this.replaceEditUrl = true;
				this.routeToEditReal(oid);
				this.replaceEditUrl = oldReplace;
			}),
		),
	);

	/**
	 * Drives the router to the edit UI for a new instance.
	 */
	readonly routeToCreateNew = this.effect((trigger$) =>
		trigger$.pipe(
			switchMap(() => {
				// An opportunity to have a custom create new (for new starter workflow)
				return this.onInsteadOfCreate();
			}),
			withLatestFrom(this.activatedRoute$),
			tap(([, activatedRoute]) => {
				assertDefined(activatedRoute, 'No activatedRoute - cannot use navigateToCreateNew');
				const oid = obfuscateNew();
				this._router.navigate([oid], {
					replaceUrl: true,
					relativeTo: activatedRoute,
					queryParamsHandling: 'merge',
				});
			}),
		),
	);

	readonly setHasUnsavedChanges = this.effect((hasUnsavedChanges$: Observable<boolean>) =>
		hasUnsavedChanges$.pipe(
			withLatestFrom(this.edit$, this.priorEdit$, this.form$),
			tap(([hasUnsavedChanges, edit, priorEdit, form]) => {
				// Prevent unsaved changes if there's no edit.
				if (!edit) {
					hasUnsavedChanges = false;
				}

				hasUnsavedChanges = this.vetoHasUnsavedChanges(hasUnsavedChanges);

				if (isDevMode() && DEBUG_DIFF) {
					const difference = diff(edit, priorEdit);
					console.log(`Diff between edit and priorEdit`, difference);
				}

				if (hasUnsavedChanges) {
					form?.markAsDirty();
				}

				this.onHasUnsavedChangesSet(hasUnsavedChanges);

				// console.log(
				// 	`%c setHasUnsavedChanges: ${hasUnsavedChanges} for ${this.niceName}`,
				// 	`color: ${hasUnsavedChanges ? 'red' : 'green'}	`,
				// );

				this.patchState({ hasUnsavedChanges });
			}),
		),
	);

	private readonly _clear = this.effect((options$?: Observable<RouteToRootOptions | undefined>) =>
		(options$ || of(undefined)).pipe(
			withLatestFrom(this.activatedRoute$, this.ignoreClear$),
			switchMap(([options, route, ignoreClear]) => {
				const doClear = () => {
					this.setIsEditing(false);
					this.setDtos(undefined);
					this.setEdit(null);
					this.setIsNewInstance(false);
					this.setOidObfuscated(undefined);

					const fromButtons = false;
					this.afterClear(fromButtons);

					const lastSelectedId = this.get().oidObfuscated;
					if (lastSelectedId) {
						this.subCancelSelection.next(lastSelectedId);
					}

					this.onRouted();
				};

				if (!route) {
					doClear();
					return EMPTY;
				}

				if (this.storeRegistry && !options?.dontSetActiveStore) {
					this.storeRegistry.setActiveStore(this);
				}

				// Don't trigger routing if forcing clear - this is already triggered by routing,
				// so routing again will result in a double navigation - a NavigationCancel event.
				if (this.isForcingClear) {
					return of(true);
				}

				if (ignoreClear) {
					this.setIgnoreClear(false);
					return of(true);
				}

				return from(
					this._router.navigate(['.'], {
						relativeTo: route,
						queryParamsHandling: 'merge',
						state: {
							// Set a flag to prevent the store-aware-can-deactivate guard from re-routing to the base URL.
							preventDeactivateReroute: true,
						},
					}),
				).pipe(
					tap((routedOk) => {
						if (routedOk !== false) {
							doClear();
						}
					}),
				);
			}),
		),
	);

	/**
	 * Clear without checking for unsaved changes.
	 */
	private readonly _cancel = this.effect((trigger$: Observable<void>) => {
		return trigger$.pipe(
			tap(() => {
				this.setIsEditing(false);
				this.setDtos(undefined);
				this.setEdit(null);
				this.setIsNewInstance(false);
				this.setOidObfuscated(undefined);

				this.afterClear();
			}),
		);
	});

	/**
	 * Clear without checking for unsaved changes.
	 */
	private readonly _destroy = this.effect((trigger$: Observable<void>) => {
		return trigger$.pipe(
			tap(() => {
				// Prevent navigating on destroy, which can trigger a NavigationCancel.
				this.setIgnoreClear(true);
				this._cancel();
				this.setIgnoreClear(false);
			}),
		);
	});

	readonly delete = this.effect((trigger$) => {
		return trigger$.pipe(
			switchMap(() => this.preventMutatingActionWhilePaintingSecurity('Delete')),
			withLatestFrom(this.edit$),
			exhaustMap(([, edit]) => {
				return this.showConfirmDeleteDialog(edit);
			}),
			withLatestFrom(this.realOid$),
			filter(([, realOid]) => realOid !== undefined),
			exhaustMap(([confirmDelete, realOid]) => {
				if (!confirmDelete) {
					this.signalDeleteSuccess(false);
					return EMPTY;
				}

				const deleteName = this.niceName;

				this.setLoadingEdit(true);
				return this.onGetDeleteParams().pipe(
					switchMap((deleteParams) =>
						this._crudAdaptor.delete(this.controllerType, realOid ?? '', deleteParams).pipe(
							tapResponse(
								(agResult) => {
									const result = agResult as SaveResult<TEdit>;
									const newList = result.dtos[1]?.objects || [];
									this.setDtos(undefined);
									this.setEdit(null);
									this.setList(newList);
									this.setOidObfuscated(undefined);

									this.signalDeleteSuccess(true);

									if (!this.suppressSnackbarMessages()) {
										this._snackbar.showMessage(`${deleteName} deleted.`);
									}

									this.onAfterSaveOrDelete(result.dtos);

									this.clear();
								},
								(err) => {
									this._genericErrorHandler.handleError(err, deleteName, 'Deleting');
									this.signalDeleteSuccess(false);
								},
							),
							finalize(() => this.setLoadingEdit(false)),
						),
					),
				);
			}),
		);
	});

	readonly deleteItem = this.effect((oid$: Observable<string>) =>
		oid$.pipe(
			exhaustMap(() => {
				return this._dialogs.showConfirmDeleteDialog(this.niceName, this.niceName.toLowerCase());
			}),
			withLatestFrom(oid$),
			exhaustMap(([confirmDelete, oid]) => {
				if (!confirmDelete) {
					return EMPTY;
				}

				this.setLoadingEdit(true);
				return this._crudAdaptor.delete(this.controllerType, oid ?? '').pipe(
					tapResponse(
						(agResult) => {
							const result = agResult as SaveResult<TEdit>;
							const newList = result.dtos[1]?.objects || [];
							this.setDtos(undefined);
							this.setEdit(null);
							this.setList(newList);
							this.setOidObfuscated(undefined);

							this._snackbar.showMessage(`${this.niceName} deleted.`);
						},
						(err) => this._genericErrorHandler.handleError(err, this.niceName, 'Deleting'),
					),
				);
			}),
		),
	);

	readonly saveWithConfirmWarning = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({ confirmWarningNextSave: true });
				this.save();
			}),
			finalize(() => {
				this.patchState({ confirmWarningNextSave: false });
			}),
		),
	);

	readonly showCantSaveMessage = this.effect((title$: Observable<string | undefined>) =>
		title$.pipe(
			tap((title) => {
				this._dialogs.showCantSaveMessage(title);
			}),
		),
	);

	readonly save = this.effect((trigger$) =>
		trigger$.pipe(
			switchMap(() => this.preventMutatingActionWhilePaintingSecurity('Save')),
			withLatestFrom(this.form$),
			switchMap(([, form]) => {
				if (!form) {
					this.saveOnceValid();
					return of(undefined);
				}

				return form.statusChanges.pipe(
					startWith(form.status),
					takeUntil(form.statusChanges.pipe(filter((status) => status !== 'PENDING'))),
					tap((status) => {
						if (status === 'PENDING') {
							this.patchState({ formPending: true });
						}
						this.saveOnceValid();
					}),
					finalize(() => {
						this.patchState({ formPending: false });
					}),
				);
			}),
		),
	);

	private readonly saveOnceValid = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.form$, this.edit$),
			delay(0),
			switchMap(([, form, edit]) => {
				if (form) {
					if (form.status === 'PENDING') {
						return EMPTY;
					}

					form.markAllAsTouched();
					form.updateValueAndValidity();

					if (form.invalid) {
						if (isDevMode()) {
							console.log(form);
						}

						this.showCantSaveMessage(`Can't save`);

						if (isDevMode()) {
							// For debugging - dump out the controls and their errors to the console.
							// TODO: Decide what to do with this. Should be be made available via the UI?
							const controlsAndErrors: Record<string, ValidationErrors> = Object.keys(
								form.controls,
							).reduce((acc, c) => {
								const ctrl = form.controls[c];
								const hasErrors = ctrl.errors && Object.keys(ctrl.errors).length > 0;
								if (hasErrors) {
									acc[c] = ctrl.errors;
								}

								return acc;
							}, {});

							console.log(controlsAndErrors);
						}

						this.signalSaveSuccess(false);
						return EMPTY;
					}
				}

				if (!edit) {
					throw new Error(`No edit (save) -  ${this.niceName} `);
				}

				return of(edit);
			}),
			switchMap((edit) => {
				// Opportunity to do something before saving.
				return this.preSave(edit);
			}),
			switchMap((edit) => {
				if (!edit) {
					this.signalSaveSuccess(false);
					return EMPTY;
				}

				return of(edit);
			}),
			switchMap((edit) => this.performSave(edit)),
		),
	);

	protected readonly handleSaveResult = this.effect((saveResult$: Observable<SaveResult<TEdit>>) =>
		saveResult$.pipe(
			withLatestFrom(this.form$),
			tapResponse(
				([agResult, form]) => {
					const result = agResult as SaveResult<TEdit>;

					this.patchState({ warnings: result.warnings || [] });

					if (!this.suppressSnackbarMessages()) {
						this._snackbar.showMessage(`${this.niceName} saved.`);
					}

					form?.markAsUntouched();
					form?.markAsPristine();

					if (!this.onHandleSaveResultAggregate(result)) {
						const newDto = result.dtos[0];
						const newList = result.dtos[1]?.objects || [];

						this.setIsNewInstance(false);
						this.setDtos(result.dtos);
						this.setEdit(newDto);
						this.setList(newList);

						this.onAfterSaveEdit(newDto);
						this.onAfterSaveOrDelete(result.dtos);
					}

					this.signalSaveSuccess(true);
				},
				(err) => {
					this._genericErrorHandler.handleError(err, this.niceName, 'Saving');
				},
			),
		),
	);

	readonly saveAndClear = this.effect((trigger$) => {
		let clearAfterSave = true;

		return trigger$.pipe(
			withLatestFrom(this.form$, this.edit$, this.allowedEditButtons$),
			delay(0),
			mergeMap(([, form, edit, allowedEditButtons]) => {
				clearAfterSave = !!allowedEditButtons.allowClear;

				if (form?.invalid) {
					form?.markAllAsTouched();

					this._dialogs.message('Please correct the errors', `Can't save`);
					return EMPTY;
				}
				assertDefined(edit);
				return of(edit);
			}),
			switchMap((edit) => {
				// Opportunity to do something before saving.
				return this.preSave(edit);
			}),
			switchMap((edit) => {
				if (!edit) {
					this.signalSaveSuccess(false);
					return EMPTY;
				}

				this.setLoadingEdit(true);

				return this.onGetSaveParams().pipe(
					switchMap((saveParams) =>
						this._crudAdaptor.save(this.controllerType, edit.ed ?? 0, edit.oid, edit, saveParams).pipe(
							tapResponse(
								(agResult) => {
									const result = agResult as SaveResult<TEdit>;

									const newList = result.dtos[1]?.objects || [];

									this.setIsEditing(false);
									this.setList(newList);

									if (clearAfterSave) {
										this.setDtos(undefined);
										this.setEdit(null);
										this.setForm(undefined);
										this.setOidObfuscated(undefined);
									}

									this._snackbar.showMessage(`${this.niceName} saved.`);

									this.onAfterSaveOrDelete(result.dtos);
									this.signalSaveSuccess(true);
								},
								(err) => {
									this._genericErrorHandler.handleError(err, this.niceName, 'Saving');
									this.signalSaveSuccess(false);
								},
							),
							finalize(() => this.setLoadingEdit(false)),
						),
					),
				);
			}),
		);
	});

	readonly copyCurrent = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.form$, this.edit$),
			exhaustMap(([, form, edit]) => {
				assertDefined(edit);

				const copy = {
					...edit,
					oid: '',
				};

				this.onCopyCurrent(copy);

				this.setEdit(copy);
				this.setIsNewInstance(true);

				this.bumpLoadCount();
				this.cancelAndClearChildStores();

				form?.reset();
				form?.markAsDirty();

				this.afterEditSet(edit);

				return EMPTY;
			}),
		),
	);

	readonly updateEdit = this.effect((updatedEdit$: Observable<TEdit>) =>
		updatedEdit$.pipe(
			withLatestFrom(this.form$),
			tap(([dto, form]) => {
				this.setUpdatedEdit(dto);
				form && form.markAsDirty();
			}),
		),
	);

	readonly markDirty = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.edit$),
			tap(([, edit]) => {
				if (!edit) {
					return;
				}
				this.updateEdit(edit);
			}),
		),
	);

	readonly handleGenericError = this.effect((error$: Observable<unknown>) => {
		return error$.pipe(
			tap((error) => {
				this._genericErrorHandler.handleError(error, this.niceName);
			}),
		);
	});

	readonly clearWarnings = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({ warnings: [] });
			}),
		),
	);

	/**
	 * Forces refreshing the current edit, ensuring that onEditSet is called, even if the edit is the same.
	 */
	readonly refreshCurrentEdit = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.edit$, this.obfuscatedOid$),
			tap(([, edit, obfuscatedOid]) => {
				if (!edit && !obfuscatedOid) {
					return;
				}

				this.setForceOnEditSetForNext(true);
				this.editOrCreate(edit?.oid ? obfuscate(edit.oid) : obfuscatedOid ?? '');
			}),
		),
	);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	getNiceName(edit: TEdit | undefined) {
		return this.niceName;
	}

	hasUnsavedChanges(): boolean {
		return !!this.get().hasUnsavedChanges;
	}

	saveWithResult() {
		this.save();
		return this.saved$.pipe(take(1));
	}

	deleteWithResult() {
		this.delete();
		return this.deleted$.pipe(take(1));
	}

	/**
	 * Triggers an editOrCreate and returns the Observable notifying of success.
	 * @param obfuscatedOid
	 * @returns
	 */
	editOrCreateWithResult(obfuscatedOid: string) {
		this.editOrCreate(obfuscatedOid);
		return this.editOrCreateComplete$;
	}

	/**
	 * Synchronously returns the current edit's OID.
	 * @returns
	 */
	getEditOid() {
		return this.get().edit?.oid;
	}

	//
	// Utility methods
	//

	protected setCrudAdaptor(adaptor: ICrudAdaptor) {
		this._crudAdaptor = adaptor;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onEditSet(edit: TEdit | null) {
		// Can be overridden to handle when the edit is set.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onAfterSaveEdit(edit: TEdit) {
		// Can be overridden to handle after the edit is successfully saved.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected afterEditSet(edit: TEdit | null) {
		// Can be overridden to handle after the edit is set, and the form has been marked pristine.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onDtosSet(dtos: ApiDtoModel[]) {
		// Can be overridden to handle when the dtos are set.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onListSet(list: TInfo[] | null) {
		// Can be overridden to handle when the list is set.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onRestore(edit: TEdit | undefined) {
		// Can be overridden to handle when the edit is restored.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onHasUnsavedChangesSet(hasUnsavedChanges: boolean) {
		// Can be overridden to handle when the edit has unsaved changes.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onCopyCurrent(copiedEdit: TEdit) {
		// Can be overridden to handle when the edit is copied.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onAfterSaveOrDelete(_dtos: unknown[]) {
		// Can be overridden to handle after the edit is saved or deleted.
	}

	protected onGetCreateParams(): Observable<Record<string, unknown>> {
		// Can be overridden to add additional parameters to the controller for a create operation.
		return of({});
	}

	protected onGetSelectParams(): Observable<Record<string, unknown>> {
		// Can be overridden to add additional parameters to the controller for a select operation.
		return of({});
	}

	protected onGetSaveParams(): Observable<Record<string, unknown>> {
		// Can be overridden to add additional parameters to the controller for a save operation.
		return of({});
	}

	protected onGetDeleteParams(): Observable<Record<string, unknown>> {
		// Can be overridden to add additional parameters to the controller for a delete operation.
		return of({});
	}

	protected onCheckSaveResult(result: SaveResult<TEdit>): Observable<SaveResult<TEdit>> {
		// Can be overridden to check the result of a save operation.
		return of(result);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onHandleSaveResultAggregate(result: SaveResult<TEdit>): boolean {
		// Allows override handling of the save result.
		return false;
	}

	/**
	 * Gets the object to save from the edit.
	 * @param edit
	 * @returns
	 */
	protected onGetSaveObject(edit: TEdit): Observable<EditDto> {
		// Can be overridden to return a different object to save.
		return of(edit);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected afterOpen(config: ScreenConfigDto) {
		// Can be overridden to handle after the open has completed.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected afterClear(fromButtons?: boolean) {
		// Can be overridden to handle after the clear has completed.
	}

	protected onRouted() {
		// Can be overridden
	}

	protected onForceClear() {
		// Can be overridden
	}

	protected onInsteadOfCreate(): Observable<unknown> {
		// Can be overridden to handle instead of create.
		return of(null);
	}

	/**
	 * Allows vetoing the value has hasUnsavedChanges
	 * @param value
	 * @returns
	 */
	protected vetoHasUnsavedChanges(value: boolean) {
		return value;
	}

	/**
	 * Template method to allow vetoing the select by returning true.
	 * @returns
	 */
	protected vetoSelect() {
		// Can be overridden to veto the select by returning true.
		return false;
	}

	protected suppressSnackbarMessages() {
		return false;
	}

	/**
	 * Method to do any transformation of the object before it is set into the state.
	 * Must return the transformed object.
	 * @param edit
	 * @returns
	 */
	protected transformEdit(edit: TEdit | null): TEdit | null {
		return edit;
	}

	protected preSave(edit: TEdit): Observable<TEdit | null> {
		return of(edit);
	}

	protected showConfirmDeleteDialog(edit: TEdit | undefined) {
		const deleteName = this.getNiceName(edit);
		return this._dialogs.showConfirmDeleteDialog(deleteName, deleteName.toLowerCase());
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected handleCustomSaveError(error: unknown) {
		// Can be overridden to handle custom errors.
		return false;
	}

	/**
	 * Causes the router to navigate to some commands in secondary outlet, relative to a provided ActivatedRoute.
	 * @returns
	 */
	protected navigateRouterToSecondaryOutlet({
		route$,
		outletName,
		commands,
		options,
	}: {
		route$: Observable<ActivatedRoute | undefined>;
		outletName: string;
		commands: unknown[];
		options?: {
			replaceUrl?: boolean;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			queryParams?: any;
			skipLocationChange?: boolean;
		};
	}) {
		return route$.pipe(
			switchMap((route) => {
				assertDefined(route);

				if (!options) {
					options = {
						replaceUrl: true,
					};
				}

				if (options.replaceUrl === undefined) {
					options.replaceUrl = true;
				}

				if (options.skipLocationChange === undefined) {
					options.skipLocationChange = true;
				}

				return navigateRouterToSecondaryOutlet({
					router: this._router,
					route,
					outletName,
					commands,
					options,
				});
			}),
		);
	}

	/**
	 * Causes the router to navigate to some commands in secondary outlet, relative to a provided ActivatedRoute.
	 */
	navigateSecondary(
		route: ActivatedRoute | undefined,
		outletName: string,
		commands: string | unknown[],
		options?: {
			replaceUrl?: boolean;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			queryParams?: any;
		},
	) {
		assertDefined(route);

		if (!options) {
			options = {
				replaceUrl: true,
			};
		}

		if (options.replaceUrl === undefined) {
			options.replaceUrl = true;
		}

		return from(navigateSecondary(this._router, route, outletName, commands, options));
	}

	init(defaultState: GenericEditState<TEdit, TInfo, TAdditionalState, TController>) {
		this.setState(defaultState);
	}

	setListExternal(list: unknown[] | null) {
		this.setList(list as TInfo[] | null);
	}

	/**
	 * Checks whether the form is valid. If invalid, shows an error dialog and returns an observable of false.
	 * If the options are provided, a flag allows returning EMPTY instead of false.
	 * If options are provided, the presence of an edit object is only checked if the validateEdit flag is true.
	 * @param opts
	 * @returns
	 */
	validateBeforeSave(opts?: { emptyIfInvalid: boolean; validateEdit?: boolean }): Observable<boolean> {
		return of(null).pipe(
			withLatestFrom(this.form$, this.edit$),
			switchMap(([, form, edit]) => {
				if (form?.invalid) {
					form?.markAllAsTouched();

					this._dialogs.message('Please correct the errors', `Can't save`);
					if (opts?.emptyIfInvalid) {
						return EMPTY;
					}
					return of(false);
				}

				if (!edit && (!opts || opts?.validateEdit)) {
					throw new Error('No edit (validateBeforeSave)');
				}

				return of(true);
			}),
		);
	}

	/**
	 * Performs the save operation. Can be overridden to provide custom save logic.
	 * @returns
	 */
	protected performSave(edit: TEdit) {
		return of(edit).pipe(
			withLatestFrom(this.confirmWarningNextSave$),
			switchMap(([edit, confirmWarning]) => {
				assertDefined(edit);

				this.setLoadingEdit(true);
				this.clearWarnings();

				return this.onGetSaveParams().pipe(
					switchMap((saveParams) => {
						if (typeof saveParams === 'object') {
							saveParams = { ...saveParams, confirmWarning: confirmWarning };
						}

						return this.onGetSaveObject(edit).pipe(
							exhaustMap((saveObject) => {
								return this._crudAdaptor
									.save(
										this.controllerType,
										saveObject.ed ?? 0,
										saveObject.oid,
										saveObject,
										saveParams,
									)
									.pipe(
										switchMap((agResult) => {
											const result = agResult as SaveResult<TEdit>;

											return this.onCheckSaveResult(result).pipe(
												map((result) => {
													this.handleSaveResult(result);
													return result;
												}),
											);
										}),
										catchError((err) => {
											// eslint-disable-next-line @typescript-eslint/no-explicit-any
											const error = (err as any).error;
											if (!error || !this.handleCustomSaveError(error)) {
												this._genericErrorHandler.handleError(err, this.niceName, 'Saving');
												this.signalSaveSuccess(false);
											}

											return EMPTY;
										}),
										finalize(() => this.setLoadingEdit(false)),
									);
							}),
						);
					}),
				);
			}),
		);
	}

	presentSingleUnsavedChangesDialog(
		lastSelectedId: string | undefined,
		opts?: { restoreOnDiscard?: boolean },
	): Observable<boolean> {
		const form = this.get().form;

		return this._dialogs
			.showSaveChangesDialog(this.niceName.toLocaleLowerCase(), form, this.saveChangesOverrideText)
			.pipe(
				switchMap((result) => {
					switch (result) {
						case SaveChangesResult.Save:
							this.save();
							//SCS-248
							this.hasCancelled = false;
							return this.saved$.pipe(
								switchMap((savedOk) => {
									return of(savedOk);
								}),
							);
						case SaveChangesResult.Discard:
							this.patchState({ hasUnsavedChanges: false });
							if (opts?.restoreOnDiscard) {
								this.restore();
							}
							//SCS-248
							this.hasCancelled = false;
							return of(true);
						default:
							this.subCancelSelection.next(lastSelectedId);
							//SCS-248
							this.hasCancelled = true;
							return of(false);
					}
				}),
			);
	}

	checkUnsavedChangesAndClear(): Observable<boolean> {
		return this.checkUnsavedChanges().pipe(
			switchMap((result) => {
				if (result) {
					this.clear();
				}
				return of(result);
			}),
		);
	}

	/**
	 * Checks for unsaved changes, and if there are any, presents a dialog to the user, asking whether to save/discard/cancel.
	 *
	 * Returns an Observable&lt;boolean&gt; representing whether the operation should proceed:
	 * 	False = user cancelled, or save failed.
	 * 	True = no changes, or user chose to discard, or save succeeded.
	 * @param opts
	 * @returns
	 */
	checkUnsavedChanges(opts?: { forceRootStore?: boolean; restoreOnDiscard?: boolean }): Observable<boolean> {
		return of(true).pipe(
			withLatestFrom(
				this.hasUnsavedChanges$,
				this.obfuscatedOid$,
				this._auth.suppressCheckUnsavedChanges$,
				this.allowSave$,
				this._appState.isPaintingSecurity$,
				this.forceAllowSaveOverride$,
			),
			// Use ExhaustMap to force handling to complete and prevent re-entry if the hasUnsavedChanges$ is still firing.
			exhaustMap(
				([
					,
					hasUnsavedChanges,
					lastSelectedId,
					suppressCheckUnsavedChanges,
					allowSave,
					isPaintingSecurity,
					forceAllowSaveOverride,
				]) => {
					if (!this._appState.isAuthenticated || suppressCheckUnsavedChanges || isPaintingSecurity) {
						// Can never have unsaved changes if not authenticated., or the user has used the Logout button from the session timeout,
						// or the user is painting security.
						return of(true);
					}

					// If the user isn't allowed to save, act as if there are no unsaved changes.
                    // SCS-204: Unsaved changes on item or Expense and click New in details tab - loses unsaved data 
					// Issue is that the allow save is false on the first time through and that allows the createNew on
                    // Calling routine in Edit-Button-Store -> addNew
                    // Do not call it here... needs to be removed
                    //if (!allowSave && !forceAllowSaveOverride) {
                    //  return of(true);
                    //}
                    if (this.storeRegistry) {
                        const rootStore = this.storeRegistry.getRootStore();
                        const activeStore =
                            opts?.forceRootStore && rootStore
                                ? rootStore
                                : this.storeRegistry.getActiveStore() || rootStore;
                        assertDefined(activeStore);
                        const childStores = this.storeRegistry?.getChildStores(activeStore);
                        // SCS-237: Doing new in contracts tab with unsaved changes on an item or expense will lose the unsaved changes
                        // Forced the parent root stopre if available as it needs to check all child stores not just the current store
                        const checkHierarchyStore = (rootStore)? rootStore : activeStore;
                        if (childStores.length) {
                            { if (typeof checkHierarchyStore.hierarchy_checkUnsavedChanges === 'function') {
                                return checkHierarchyStore.hierarchy_checkUnsavedChanges();
                            }}
                        }
                        // SCS-237: Changed the above to use checkHierarchyStore instead
                    }
                    // SCS-204: Unsaved changes on item or Expense and click New in details tab - loses unsaved data
                    // Calling routine in Edit-Button-Store -> addNew
                    // Moved to after hierarchy_checkUnsavedChanges is called
                    if (!allowSave && !forceAllowSaveOverride) {
                        return of(true);
                    }
                    if (!hasUnsavedChanges) {
                        return of(true);
                    }

					return this.presentSingleUnsavedChangesDialog(lastSelectedId, {
						restoreOnDiscard: opts?.restoreOnDiscard,
					});
				},
			),
		);
	}

	hierarchy_checkUnsavedChanges(): Observable<boolean> {
		assertDefined(this.storeRegistry);
        // SCS-203: No Prompt for Save on Staff Details when other tabs also changed
        // The parent store is the details store and this was not included in the child stores
        // collection, resulting in the changes not being picked up in certain situations.
        const childStores = this.storeRegistry.getChildStoresRecursive(this);
            const parentStore = this.storeRegistry.getRootStore();
            if (parentStore)
                if (!childStores.includes(parentStore))
                {
                    childStores.push(parentStore);
                }
        // SCS-203: No Prompt for Save on Staff Details when other tabs also changed

        // Do any child store have unsaved changes?
		const anyHaveChanges =
			this.storeRegistry.hasUnsavedChanges ||
			childStores.some((store) => {
				const hasChanges = store.hasUnsavedChanges();
				if (DEBUG_CHANGES && hasChanges) {
					console.log('hierarchy_checkUnsavedChanges hasChanges', store.niceName);
				}
				return hasChanges;
			});

		const childStoresWithUnsavedChanges = childStores.filter((store) => store.hasUnsavedChanges());

		if (!anyHaveChanges) {
			/**
			 * Check this store - if it has unsaved changes, show a dialog asking if the user wants to save or discard, otherwise return true -
			 * no changes to save.
			 */
			if (!this.hasUnsavedChanges()) {
				return of(true);
			} else {
				if (DEBUG_CHANGES) {
					console.log('hierarchy_checkUnsavedChanges has changes', this.niceName);
				}

				const lastSelectedId = this.get().oidObfuscated;
				return this.presentSingleUnsavedChangesDialog(lastSelectedId);
			}
		}

		/**
		 * If there is only one child store with unsaved changes, and it is the active store,
		 * and there's no registry-level unsaved changes message override, then show the dialog
		 * for that store, which allows the user to directly save or discard the changes (or cancel).
		 */
		if (
			childStoresWithUnsavedChanges.length === 1 &&
			this.storeRegistry?.getActiveStore() === childStoresWithUnsavedChanges[0] &&
			!this.storeRegistry?.hasUnsavedChangesMessageOverride
		) {
			const store = childStoresWithUnsavedChanges[0] as IStore_Internal;
			const lastSelectedId = store.oidObfuscated;
			return store.presentSingleUnsavedChangesDialog(lastSelectedId);
		}

		/**
		 * There are unsaved changes.
		 * Allow the StoreRegistry to use an override message if it wants to. This is for existing workflows.
		 */
		return this.storeRegistry.showUnsavedChangesMessageOverride().pipe(
			switchMap((result) => {
				if (result) {
					// It was handled by the StoreRegister (there was an override)
					switch (result) {
						case SaveChangesResult.Discard:
							this.storeRegistry?.discardAllChanges();
							return of(true);
						default: {
							const lastSelectedId = this.get().oidObfuscated;
							this.subCancelSelection.next(lastSelectedId);
							return of(false);
						}
					}
				} else {
					/**
					 * No override. Show a dialog showing the store or stores that are dirty and tell the user they must each
					 * be saved individually, or all changes discarded.
					 */
					let dirtyStores = childStores.filter((store) => store.hasUnsavedChanges());
					let dirtyStoreNames = dirtyStores.map((s) => s.tabName || s.niceName);

					if (this.hasUnsavedChanges()) {
						dirtyStores = [this, ...dirtyStores];
						dirtyStoreNames = [this.tabName || this.niceName, ...dirtyStoreNames].map((s) =>
							s.toLocaleLowerCase(),
						);
					}

					return this._dialogs.showMultipleUnsavedChangesDialog(dirtyStoreNames).pipe(
						switchMap((result) => {
							switch (result) {
								case SaveChangesResult.Discard:
									this.storeRegistry?.discardAllChanges();
									return of(true);
								default: {
									const lastSelectedId = this.get().oidObfuscated;
									this.subCancelSelection.next(lastSelectedId);
									return of(false);
								}
							}
						}),
					);
				}
			}),
		);
	}

	/**
	 * Returns an Observable of the URL being the baseUrl combined with the current obfuscated oid, if any.
	 * @param baseUrl
	 * @returns
	 */
	constructUrl$(baseUrl: string): Observable<string> {
		return this.obfuscatedOid$.pipe(
			map((obfuscatedOid) => {
				const oidPart = obfuscatedOid ? `/${obfuscatedOid}` : '';
				return `${baseUrl}${oidPart}`;
			}),
		);
	}

	ngrxOnStoreInit() {
		// Determine screen access level to apply to all stores for the screen.
		const access = getAccessLevelForCurrentScreen(this._router, this._userAccess);
		this.patchState({
			screenAccessLevel: access,
		});
	}

	/**
	 * Synchronously gets the current URL.
	 */
	getUrl() {
		const { url } = this.get();
		return url;
	}

	/**
	 * Synchronously returns the currently selected "real" OID.
	 * @returns
	 */
	getSelectedOid(): string | undefined {
		const { createOid, edit, isNewInstance } = this.get();
		if (edit) {
			if (isNewInstance) {
				return createOid + '';
			} else {
				return edit.oid;
			}
		}

		return '';
	}

	/**
	 * Synchronously returns the current edit object.
	 * @returns
	 */
	getEdit(): TEdit | undefined {
		const { edit } = this.get();
		return edit;
	}

	/**
	 * Synchronously gets whether the current edit has unsaved changes.
	 */
	getHasUnsavedChanges() {
		const { hasUnsavedChanges } = this.get();
		return hasUnsavedChanges;
	}

	forceResetOid() {
		// Bump the selectCount to cause the realOid$ to be recalculated.
		let { selectCount } = this.get();
		selectCount = selectCount ?? 0;
		this.patchState({
			selectCount: ++selectCount,
		});

		// Reset up the chain to the parent.
		const parentStore = this.storeRegistry?.getParentStore(this);
		if (parentStore) {
			parentStore.forceResetOid();
		}
	}

	setOidObfuscated(oidObfuscated: string | undefined) {
		this._setOidObfuscated(oidObfuscated);
	}

	clear(options?: RouteToRootOptions) {
		this._clear(options);
	}

	cancel() {
		this._cancel();
	}

	destroy() {
		this._destroy();
	}

	setEdit(edit: TEdit | null) {
		this._setEdit(edit);
	}

	/**
	 * Handles destroying the store, and deregistering it from the global registry.
	 */
	override ngOnDestroy() {
		super.ngOnDestroy();

		this._globalRegistry.deregisterStore(this);
	}

	protected signalSaveSuccess(wasSuccessful: boolean) {
		this.subSaved.next(wasSuccessful);
	}

	protected signalDeleteSuccess(wasSuccessful: boolean) {
		this.subDeleted.next(wasSuccessful);
	}

	protected signalEditOrCreateComplete(wasSuccessful: boolean) {
		this.subEditOrCreateComplete.next(wasSuccessful);
		this.subEditOrCreateComplete.complete();

		this.subEditOrCreateComplete = new Subject<boolean>();
		this.editOrCreateComplete$ = this.subEditOrCreateComplete.asObservable();
	}

	private protectUnsavedChangesFromBecomingTrueOnReadOnlyScreen(hasUnsavedChanges: boolean): boolean {
		const screenReadOnly = this.get().screenAccessLevel === AccessLevels.ReadOnly;
		if (screenReadOnly) {
			return false;
		}

		return hasUnsavedChanges;
	}

	protected cancelAndClearChildStores() {
		const childStoreEntries = this.storeRegistry?.getChildStores(this) || [];
		childStoreEntries.forEach((entry) => {
			entry.store.cancel();
			entry.store.clear({ dontSetActiveStore: true });
		});
	}

	/**
	 * Takes the options property, which is a Record<string, unknown>, and casts it to the specified type.
	 * @returns
	 */
	protected castOptionsAs<T>() {
		return (this.options || {}) as unknown as T;
	}
}
